import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import AppLayout from '../layouts/AppLayout';
import AuthLayout from '../layouts/AuthLayout';
import { NotFoundPage } from '../pages/ErrorPages';
import {
  LoginPage, ForgotPasswordPage,
  ProfilePage, ModulePlayerPage,
  ModuleEditorListPage, ModuleEditorPage, UploadPage,
  ModuleAnalyticsPage,
} from '../pages';
import AnalyticsHubPage from '../pages/AnalyticsHubPage';
import QuestionBanksPage from '../pages/QuestionBanksPage';
import QuizReviewQueuePage from '../pages/QuizReviewQueuePage';
import LiveJoinPage from '../pages/LiveJoinPage';
import LiveHostPage from '../pages/LiveHostPage';
import SettingsLayout, { SettingsIndexRedirect } from '../pages/settings/SettingsLayout';
import OrganizationsTab from '../pages/settings/OrganizationsTab';
import UsersAccessTab from '../pages/settings/UsersAccessTab';
import BrandingPreferencesTab from '../pages/settings/BrandingPreferencesTab';
import StorageFilesTab from '../pages/settings/StorageFilesTab';
import ActivityLogsTab from '../pages/settings/ActivityLogsTab';
import SecurityTab from '../pages/settings/SecurityTab';
import DataHealthTab from '../pages/settings/DataHealthTab';
import MyPreferencesPage from '../pages/MyPreferencesPage';
import RolesPermissionsTab from '../pages/settings/RolesPermissionsTab';
import ScheduledReportsTab from '../pages/settings/ScheduledReportsTab';
import QuizEditorPage from '../pages/QuizEditorPage';
import VideoLibraryPage from '../pages/VideoLibraryPage';
import ScormLibraryPage from '../pages/ScormLibraryPage';
import TraineeManagementPage from '../pages/trainees/TraineeManagementPage';
import LearningPathsPage from '../pages/paths/LearningPathsPage';
import DocumentsPage from '../pages/documents/DocumentsPage';
import AssignmentsPage from '../pages/assignments/AssignmentsPage';
import SkillMatrixPage from '../pages/skills/SkillMatrixPage';
import BatchDetailPage from '../pages/trainees/BatchDetailPage';
import TrainingCalendarPage from '../pages/calendar/TrainingCalendarPage';
import NotificationCenterPage from '../pages/notifications/NotificationCenterPage';
import AuditLogPage from '../pages/AuditLogPage';
import CertVerifyPage from '../pages/CertVerifyPage';
import FeedbackSubmitPage from '../pages/FeedbackSubmitPage';
import AgentLayout from '../layouts/AgentLayout';
import MyLearningPage from '../pages/agent/MyLearningPage';
import AssignmentPlayerPage from '../pages/agent/AssignmentPlayerPage';
import MyAssessmentsPage from '../pages/agent/MyAssessmentsPage';
import AssessmentTakePage from '../pages/agent/AssessmentTakePage';
import MyHistoryPage from '../pages/agent/MyHistoryPage';
import { useAuth } from '../contexts/AuthContext';

/** Role-aware landing: agents → My Learning, everyone else → Content. */
function HomeRedirect() {
  const { user, isLoading } = useAuth();
  // Wait for /me to resolve — redirecting before the role is known briefly
  // sends trainees to /editor/modules and flashes a 403.
  if (isLoading || !user) return null;
  if (user.role === 'trainee') return <Navigate to="/my-learning" replace />;
  return <Navigate to="/editor/modules" replace />;
}

/**
 * HostRouter — Phase 4 short-URL contract for `lumyn.live`.
 *
 * When the app is loaded on the `lumyn.live` host, any path outside the
 * public join/verify flow is force-redirected to the `/live` branded splash.
 * Deep-links like `lumyn.live/ZF7K42` pre-fill the 6-digit code. This is a
 * frontend-only contract; Nginx just needs to proxy lumyn.live to the same
 * frontend upstream (see `/app/memory/phase4_nginx.conf`).
 *
 * Uses `<Navigate>` instead of history.replaceState so React Router actually
 * re-matches after the redirect.
 */
function HostRouter({ children }) {
  const location = useLocation();
  if (typeof window === 'undefined') return children;
  const isLiveHost = /(^|\.)lumyn\.live$/i.test(window.location.hostname);
  if (!isLiveHost) return children;

  const path = location.pathname;
  const isLive = path === '/live' || path.startsWith('/live/');
  if (isLive) return children;

  const seg = path.replace(/^\//, '').split('/')[0];
  const target = seg ? `/live/${seg}` : '/live';
  return <Navigate to={target} replace />;
}

export default function AppRoutes() {
  return (
    <HostRouter>
      <AppRoutesInner />
    </HostRouter>
  );
}

function AppRoutesInner() {
  return (
    <Routes>
      {/* Public: Auth */}
      {/* Login — full-page "Switch On" design (variant 6) */}
      <Route path="/login" element={<LoginPage />} />
      <Route element={<AuthLayout />}>
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      </Route>

      {/* Live join — full-immersive (own dark canvas) */}
      <Route path="/live" element={<LiveJoinPage />} />
      <Route path="/live/:code" element={<LiveJoinPage />} />

      {/* Public — no auth: certificate verification + feedback submission */}
      <Route path="/verify/cert/:code" element={<CertVerifyPage />} />
      <Route path="/feedback/:code" element={<FeedbackSubmitPage />} />

      {/* Full-screen presentation (no sidebar) */}
      <Route path="modules/:id/learn" element={<ProtectedRoute><ModulePlayerPage /></ProtectedRoute>} />

      {/* AGENT (trainee) portal — own minimal layout */}
      <Route element={<ProtectedRoute><AgentLayout /></ProtectedRoute>}>
        <Route path="my-learning" element={<MyLearningPage />} />
        <Route path="my-learning/:assignmentId" element={<AssignmentPlayerPage />} />
        <Route path="my-assessments" element={<MyAssessmentsPage />} />
        <Route path="my-assessments/:assessmentId/take" element={<AssessmentTakePage />} />
        <Route path="my-history" element={<MyHistoryPage />} />
      </Route>

      {/* Authenticated routes (with sidebar layout) */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route index element={<HomeRedirect />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="preferences" element={<MyPreferencesPage />} />

        {/* CONTENT — list + read pages open to trainer (view-only); write
            actions inside each page are gated by hasRole('editor') so the
            UI mirrors backend `require_editor` enforcement. */}
        <Route path="editor/modules" element={<ProtectedRoute minRole="trainer"><ModuleEditorListPage /></ProtectedRoute>} />
        <Route path="editor/modules/:id" element={<ProtectedRoute minRole="trainer"><ModuleEditorPage /></ProtectedRoute>} />
        <Route path="editor/banks" element={<ProtectedRoute minRole="trainer"><QuestionBanksPage /></ProtectedRoute>} />
        <Route path="editor/banks/:bankId" element={<ProtectedRoute minRole="trainer"><QuestionBanksPage /></ProtectedRoute>} />
        <Route path="editor/library/videos" element={<ProtectedRoute minRole="editor"><VideoLibraryPage /></ProtectedRoute>} />
        <Route path="editor/library/courses" element={<ProtectedRoute minRole="trainer"><ScormLibraryPage /></ProtectedRoute>} />

        {/* WRITE-ONLY surfaces — editor+ required */}
        <Route path="editor/modules/upload" element={<ProtectedRoute minRole="editor"><UploadPage /></ProtectedRoute>} />
        <Route path="editor/modules/:moduleId/quizzes/:quizId" element={<ProtectedRoute minRole="editor"><QuizEditorPage /></ProtectedRoute>} />
        <Route path="editor/quizzes/:quizId/review" element={<ProtectedRoute minRole="editor"><QuizReviewQueuePage /></ProtectedRoute>} />

        {/* PRESENTATION / LIVE — trainer+ (includes editors) */}
        <Route path="editor/live" element={<ProtectedRoute minRole="trainer"><LiveHostPage /></ProtectedRoute>} />

        {/* TRAINEE MANAGEMENT — trainer+ for read/CRUD; field/batch admin gated inside */}
        <Route path="trainees" element={<ProtectedRoute minRole="trainer"><TraineeManagementPage /></ProtectedRoute>} />
        <Route path="paths" element={<ProtectedRoute minRole="viewer"><LearningPathsPage /></ProtectedRoute>} />
        <Route path="documents" element={<ProtectedRoute minRole="viewer"><DocumentsPage /></ProtectedRoute>} />
        <Route path="assignments" element={<ProtectedRoute minRole="viewer"><AssignmentsPage /></ProtectedRoute>} />
        <Route path="skills" element={<ProtectedRoute minRole="viewer"><SkillMatrixPage /></ProtectedRoute>} />
        <Route path="calendar" element={<ProtectedRoute minRole="viewer"><TrainingCalendarPage /></ProtectedRoute>} />
        <Route path="notifications" element={<ProtectedRoute minRole="viewer"><NotificationCenterPage /></ProtectedRoute>} />
        <Route path="trainees/batches/:batchId" element={<ProtectedRoute minRole="trainer"><BatchDetailPage /></ProtectedRoute>} />

        {/* AUDIT LOG — org_admin+ only */}
        <Route path="audit" element={<ProtectedRoute minRole="org_admin"><AuditLogPage /></ProtectedRoute>} />

        {/* Analytics Hub — trainer+ (per Q4=b, no per-host scoping) */}
        <Route path="analytics" element={<ProtectedRoute minRole="trainer"><AnalyticsHubPage /></ProtectedRoute>} />
        <Route path="trainer/analytics/:moduleId" element={<ProtectedRoute minRole="trainer"><ModuleAnalyticsPage /></ProtectedRoute>} />

        {/* Legacy redirects — keep external links working */}
        <Route path="modules" element={<Navigate to="/editor/modules" replace />} />
        <Route path="modules/:id" element={<Navigate to="/editor/modules" replace />} />
        <Route path="certificates" element={<Navigate to="/editor/modules" replace />} />
        <Route path="trainer" element={<Navigate to="/analytics" replace />} />
        <Route path="trainer/analytics" element={<Navigate to="/analytics" replace />} />
        <Route path="trainer/enrollments" element={<Navigate to="/editor/modules" replace />} />
        <Route path="editor/pkt" element={<Navigate to="/analytics" replace />} />
        <Route path="admin/users" element={<Navigate to="/settings/users" replace />} />
        <Route path="admin/org" element={<Navigate to="/settings/branding" replace />} />
        <Route path="admin/analytics" element={<Navigate to="/analytics" replace />} />

        {/* ADMIN — Settings */}
        <Route path="settings" element={<ProtectedRoute minRole="org_admin"><SettingsLayout /></ProtectedRoute>}>
          <Route index element={<SettingsIndexRedirect />} />
          <Route path="organizations" element={<ProtectedRoute minRole="admin"><OrganizationsTab /></ProtectedRoute>} />
          <Route path="users" element={<UsersAccessTab />} />
          <Route path="roles" element={<ProtectedRoute minRole="org_admin"><RolesPermissionsTab /></ProtectedRoute>} />
          <Route path="branding" element={<BrandingPreferencesTab />} />
          <Route path="security" element={<SecurityTab />} />
          <Route path="data-health" element={<DataHealthTab />} />
          <Route path="storage" element={<ProtectedRoute minRole="admin"><StorageFilesTab /></ProtectedRoute>} />
          <Route path="logs" element={<ActivityLogsTab />} />
          <Route path="notifications" element={<Navigate to="/notifications" replace />} />
          <Route path="reports" element={<ScheduledReportsTab />} />
        </Route>
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
